import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select
} from "@mui/material";
import { format, parseISO, isAfter, addHours } from "date-fns";
import { fr } from "date-fns/locale";
import Constants from "../../../../utils/constants";
import { DateTimePicker } from "@mui/x-date-pickers";
import Grid from "@mui/material/Grid2";

function FdtCreateModal({ open, handleClose, data, onCreateEmpty }) {
    const [statutJournee, setStatutJournee] = useState("WD"); // Default to "Travail"
    const [heureDebut, setHeureDebut] = useState(null);
    const [heureFin, setHeureFin] = useState(null);
    const [tempsPauseMinutes, setTempsPauseMinutes] = useState(0); // Default to no pause
    const [errors, setErrors] = useState({});
    const [isValid, setIsValid] = useState(false);

    // Initialize default values when the modal opens or data changes
    useEffect(() => {
        if (data && data.date) {
            // Set default values: 9:00 AM, no pause, 4:00 PM
            const dateObj = new Date(data.date);
            const debutDate = new Date(dateObj.setHours(9, 0, 0, 0));
            const finDate = new Date(dateObj.setHours(16, 0, 0, 0));

            setHeureDebut(debutDate);
            setHeureFin(finDate);
            setTempsPauseMinutes(0);

            // Validate initial values
            validateForm(debutDate, finDate, 0);
        }
    }, [data, open]);

    // Validate form data
    const validateForm = (debut, fin, pause) => {
        const newErrors = {};

        if (!debut) {
            newErrors.heureDebut = "L'heure de début est requise";
        }

        if (!fin) {
            newErrors.heureFin = "L'heure de fin est requise";
        } else if (debut && isAfter(debut, fin)) {
            newErrors.heureFin = "L'heure de fin doit être postérieure à l'heure de début";
        } else if (debut && isAfter(fin, addHours(debut, 24))) {
            newErrors.heureFin = "L'heure de fin ne doit pas être plus de 24 heures après l'heure de début";
        }

        if (pause === undefined || pause === null) {
            newErrors.tempsPauseMinutes = "Le temps pause est requis";
        }

        setErrors(newErrors);
        setIsValid(Object.keys(newErrors).length === 0);
    };

    const handleCreate = () => {
        if (data && data.operateurId && data.date && isValid) {
            onCreateEmpty(
                data.operateurId, 
                data.date, 
                statutJournee, 
                format(heureDebut, 'yyyy-MM-dd HH:mm:ss'),
                format(heureFin, 'yyyy-MM-dd HH:mm:ss'),
                tempsPauseMinutes
            );
        }
    };

    const handleStatutJourneeChange = (event, newValue) => {
        if (newValue !== null && data && data.date) {
            setStatutJournee(newValue);
            
            const dateObj = new Date(data.date);
            const debutDate = new Date(dateObj);
            debutDate.setHours(9, 0, 0, 0);
            
            const finDate = new Date(dateObj);
            // Pour AM et Repos, on met automatiquement 0h (9h-9h), sinon 16h par défaut
            const isZeroHours = newValue === "AM" || newValue === "Repos";
            finDate.setHours(isZeroHours ? 9 : 16, 0, 0, 0);
            
            setHeureDebut(debutDate);
            setHeureFin(finDate);
            setTempsPauseMinutes(0);
            validateForm(debutDate, finDate, 0);
        }
    };

    const handleHeureDebutChange = (value) => {
        setHeureDebut(value);
        validateForm(value, heureFin, tempsPauseMinutes);
    };

    const handleHeureFinChange = (value) => {
        setHeureFin(value);
        validateForm(heureDebut, value, tempsPauseMinutes);
    };

    const handleTempsPauseChange = (event) => {
        const value = event.target.value;
        setTempsPauseMinutes(value);
        validateForm(heureDebut, heureFin, value);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {data && `${data.operateur} - ${format(new Date(data.date), "d MMMM yyyy", { locale: fr })}`}
            </DialogTitle>
            <DialogContent>
                {data && (
                    <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ mb: 3 }}>
                            Vous êtes sur le point de créer une feuille de temps pour cet opérateur à cette date.
                        </Typography>

                        {/* Type de journée */}
                        <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                            Type de journée
                        </Typography>
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            size="medium"
                            value={statutJournee}
                            onChange={handleStatutJourneeChange}
                            aria-label="statut de la journée"
                            sx={{ mt: 1, mb: 3, flexWrap: 'wrap' }}
                        >
                            {Constants.RH_STATUT_JOURNEE_DATA.map((option) => (
                                <ToggleButton
                                    key={option.id}
                                    value={option.id}
                                    style={{
                                        backgroundColor: statutJournee === option.id ? option.color : undefined,
                                        color: statutJournee === option.id ? option.fontColor : undefined,
                                    }}
                                    className="toggle-button"
                                >
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        {/* Heure de début */}
                        <Grid container size={12} spacing={2} sx={{ mb: 2 }}>
                            <Grid size={4}>
                                <Typography variant="body1" gutterBottom>
                                    Heure de début <span style={{ color: 'red' }}>*</span>
                                </Typography>
                            </Grid>
                            <Grid size={8}>
                                <FormControl fullWidth error={Boolean(errors.heureDebut)}>
                                    <DateTimePicker
                                        value={heureDebut}
                                        onChange={handleHeureDebutChange}
                                        slotProps={{
                                            textField: {
                                                variant: 'outlined',
                                                fullWidth: true,
                                                error: Boolean(errors.heureDebut)
                                            }
                                        }}
                                    />
                                    {errors.heureDebut && (
                                        <FormHelperText error>{errors.heureDebut}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* Temps de pause */}
                        <Grid container size={12} spacing={2} sx={{ mb: 2 }}>
                            <Grid size={4}>
                                <Typography variant="body1" gutterBottom>
                                    Temps de pause <span style={{ color: 'red' }}>*</span>
                                </Typography>
                            </Grid>
                            <Grid size={8}>
                                <FormControl fullWidth error={Boolean(errors.tempsPauseMinutes)}>
                                    <InputLabel id="label-pause">Indiquer le temps de pause</InputLabel>
                                    <Select
                                        labelId="label-pause"
                                        value={tempsPauseMinutes}
                                        onChange={handleTempsPauseChange}
                                        label="Indiquer le temps de pause"
                                    >
                                        {Constants.TEMPS_PAUSE_MINUTES_DATA.map((p) => (
                                            <MenuItem key={p.value} value={p.value}>
                                                {p.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.tempsPauseMinutes && (
                                        <FormHelperText error>{errors.tempsPauseMinutes}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* Heure de fin */}
                        <Grid container size={12} spacing={2} sx={{ mb: 2 }}>
                            <Grid size={4}>
                                <Typography variant="body1" gutterBottom>
                                    Heure de fin <span style={{ color: 'red' }}>*</span>
                                </Typography>
                            </Grid>
                            <Grid size={8}>
                                <FormControl fullWidth error={Boolean(errors.heureFin)}>
                                    <DateTimePicker
                                        value={heureFin}
                                        onChange={handleHeureFinChange}
                                        slotProps={{
                                            textField: {
                                                variant: 'outlined',
                                                fullWidth: true,
                                                error: Boolean(errors.heureFin)
                                            }
                                        }}
                                    />
                                    {errors.heureFin && (
                                        <FormHelperText error>{errors.heureFin}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="red">
                    Annuler
                </Button>
                <Button 
                    onClick={handleCreate} 
                    color="primary" 
                    variant="contained"
                    disabled={!isValid}
                >
                    Créer la feuille de temps
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default FdtCreateModal;
